import { useForm } from 'react-hook-form';
import { useCallback, useEffect } from 'react';
import { WorkflowFormData, StepTypeEnum, FindByEnum, Workflow } from '../types/workflow.types';
import { requiresSelector } from '@const/workflow.constants';

export interface UseWorkflowFormProps {
  defaultValues?: WorkflowFormData | Workflow;
  onSubmit: (data: WorkflowFormData) => void;
}

const getInitialValues = (): WorkflowFormData => {
  const stepType = StepTypeEnum.NAVIGATE;
  return {
    name: '',
    steps: [
      {
        order: 1,
        step: {
          type: stepType,
          // Always include find, with empty strings if step type doesn't require selector
          find: requiresSelector(stepType)
            ? {
                by: FindByEnum.CSS,
                value: '',
              }
            : {
                by: '',
                value: '',
              },
        },
        delayMs: 1000,
      },
    ],
    mappings: undefined,
    condition: undefined,
  };
};

export const useWorkflowForm = ({ defaultValues, onSubmit }: UseWorkflowFormProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    setValue,
    watch,
    trigger,
  } = useForm<WorkflowFormData>({
    defaultValues: getInitialValues(),
    mode: 'onChange',
    reValidateMode: 'onSubmit',
  });

  // Reset form when defaultValues change (e.g., when data loads)
  useEffect(() => {
    if (defaultValues) {
      // Extract only the form fields from the defaultValues
      const formData: WorkflowFormData = {
        name: defaultValues.name,
        steps: defaultValues.steps,
        mappings: defaultValues.mappings
          ? {
              ...defaultValues.mappings,
              // Ensure fieldsMap is always an object, never undefined or null
              fieldsMap: defaultValues.mappings.fieldsMap || {},
            }
          : undefined,
        condition: defaultValues.condition,
      };
      reset(formData);
    }
  }, [defaultValues, reset]);

  const resetForm = useCallback(() => {
    reset();
  }, [reset]);

  return {
    control,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    resetForm,
    setError,
    setValue,
    watch,
    trigger,
  };
};
